import { Controller } from '@hotwired/stimulus'
import { post } from '@rails/request.js'

export default class extends Controller {
  static targets = ['input', 'hidden', 'dropdown', 'option', 'createOption', 'createLabel', 'empty']
  static values = { createUrl: String, paramKey: String }

  connect() {
    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener('mousedown', this.boundClickOutside)
  }

  disconnect() {
    document.removeEventListener('mousedown', this.boundClickOutside)
  }

  open() {
    this.dropdownTarget.classList.remove('hidden')
    this.filter()
  }

  close() {
    this.dropdownTarget.classList.add('hidden')
    const matched = this.findOptionByLabel(this.inputTarget.value.trim())
    if (!matched) {
      this.hiddenTarget.value = ''
      if (!this.inputTarget.value.trim()) this.inputTarget.value = ''
    }
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  filter() {
    const query = this.inputTarget.value.trim().toLowerCase()
    let visibleCount = 0
    this.optionTargets.forEach((option) => {
      const matches = !query || option.dataset.label.toLowerCase().includes(query)
      option.classList.toggle('hidden', !matches)
      if (matches) visibleCount++
    })

    if (this.hasCreateOptionTarget) {
      const exact = this.findOptionByLabel(this.inputTarget.value.trim())
      const showCreate = this.hasCreateUrlValue && query && !exact
      this.createOptionTarget.classList.toggle('hidden', !showCreate)
      if (showCreate) this.createLabelTarget.textContent = this.inputTarget.value.trim()
    }

    if (this.hasEmptyTarget) {
      const createVisible = this.hasCreateOptionTarget && !this.createOptionTarget.classList.contains('hidden')
      this.emptyTarget.classList.toggle('hidden', visibleCount > 0 || createVisible)
    }
  }

  findOptionByLabel(label) {
    if (!label) return null
    const lower = label.toLowerCase()
    return this.optionTargets.find((o) => o.dataset.label.toLowerCase() === lower)
  }

  select(event) {
    event.preventDefault()
    const button = event.currentTarget
    this.applySelection(button.dataset.value, button.dataset.label)
    this.close()
  }

  async createNew(event) {
    event.preventDefault()
    const name = this.inputTarget.value.trim()
    if (!name) return

    const response = await post(this.createUrlValue, {
      body: { [this.paramKeyValue]: { name } },
      responseKind: 'json'
    })

    if (response.ok) {
      const data = await response.json
      const button = document.createElement('button')
      button.type = 'button'
      button.dataset.action = 'mousedown->searchable-select#select'
      button.dataset.value = data.id
      button.dataset.label = data.name
      button.dataset.searchableSelectTarget = 'option'
      button.className = 'block w-full text-left px-3 py-1.5 text-sm text-text hover:bg-raised cursor-pointer'
      button.textContent = data.name
      this.dropdownTarget.insertBefore(button, this.createOptionTarget)
      this.applySelection(data.id, data.name)
      this.close()
    }
  }

  applySelection(value, label) {
    this.hiddenTarget.value = value
    this.inputTarget.value = label
  }

  clear(event) {
    event?.preventDefault()
    this.hiddenTarget.value = ''
    this.inputTarget.value = ''
    this.inputTarget.focus()
  }

  keydown(event) {
    if (event.key === 'Escape') {
      this.close()
      this.inputTarget.blur()
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      const visible = this.optionTargets.find((o) => !o.classList.contains('hidden'))
      if (visible) {
        this.applySelection(visible.dataset.value, visible.dataset.label)
        this.close()
      } else if (this.hasCreateOptionTarget && !this.createOptionTarget.classList.contains('hidden')) {
        this.createNew(event)
      }
    }
  }
}
